import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';
import { siteInfo } from '@/mocks/home';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    try {
      const response = await fetch('https://readdy.ai/api/form/d9u06il3pcjqs2fcsjt0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        if (msg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const milestones = [
    { year: '2020', title: '平台成立', desc: '亚洲在线正式上线，服务亚洲用户' },
    { year: '2021', title: '用户突破100万', desc: '平台用户快速增长，内容生态初步建立' },
    { year: '2022', title: '业务拓展', desc: '新增跨境电商和生活服务板块' },
    { year: '2023', title: '区域深耕', desc: '进入东南亚市场，建立本地化运营团队' },
    { year: '2024', title: '用户突破500万', desc: '月活跃用户突破500万，覆盖30+国家和地区' },
    { year: '2025', title: '内容升级', desc: '推出AI个性化推荐，内容质量全面升级' },
  ];

  const orgSchema = {
    name: '亚洲在线',
    url: 'https://hjmiyhg.cn',
    description: siteInfo.description,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: siteInfo.phone,
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CN',
      addressLocality: '上海',
      streetAddress: '浦东新区陆家嘴金融贸易区世纪大道100号',
    },
    foundingDate: '2020',
  };

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hjmiyhg.cn' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <SchemaOrg type="Organization" data={orgSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <section className="bg-background-100 py-14 md:py-18 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6">
                <a href="/" className="hover:text-primary-600 transition-colors duration-200 cursor-pointer">首页</a>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800">关于我们</span>
              </nav>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">关于我们</h1>
              <p className="text-foreground-600 text-sm md:text-base">了解亚洲在线的品牌故事与发展历程</p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="py-16 md:py-20 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                <div>
                  <span className="inline-block px-4 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                    品牌介绍
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-5">
                    {siteInfo.name}
                  </h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    {siteInfo.description}
                  </p>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    自2020年成立以来，亚洲在线始终秉持"连接亚洲、服务用户"的使命，致力于为亚洲用户打造一个集资讯、娱乐、购物、生活服务于一体的综合性在线平台。我们汇聚来自亚洲各地的优质内容创作者和服务提供商，通过严格的审核机制确保内容的真实性、可靠性和高质量。
                  </p>
                  <p className="text-foreground-600 leading-relaxed">
                    目前，亚洲在线已覆盖30多个亚洲国家和地区，拥有超过500万活跃用户。我们的团队由来自亚洲各国的专业人才组成，深刻理解本地用户需求，为不同地区的用户提供本地化、个性化的服务体验。
                  </p>
                </div>
                <div className="relative h-80 md:h-96 rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20Asian%20office%20team%20collaborating%20around%20a%20large%20digital%20display%20showing%20data%2C%20warm%20natural%20lighting%20from%20large%20windows%2C%20clean%20minimalist%20workspace%20with%20wooden%20elements%2C%20professional%20corporate%20photography%20with%20soft%20atmosphere&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="亚洲在线团队"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                {[
                  { value: siteInfo.stats.users, label: '活跃用户' },
                  { value: siteInfo.stats.articles, label: '优质内容' },
                  { value: siteInfo.stats.countries, label: '覆盖国家' },
                  { value: siteInfo.stats.partners, label: '合作伙伴' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className="font-heading text-2xl md:text-3xl text-primary-600 mb-2">{stat.value}</p>
                    <p className="text-sm text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="py-16 md:py-20 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12 md:mb-14">
                <span className="inline-block px-4 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                  发展历程
                </span>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
                  我们的成长之路
                </h2>
              </div>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
                <div className="flex flex-col gap-8">
                  {milestones.map((m, i) => (
                    <div key={m.year} className={`flex items-start gap-6 md:gap-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                        <div className={`${i % 2 === 0 ? 'md:pr-10' : 'md:pl-10'}`}>
                          <h3 className="font-heading text-lg text-foreground-950 mb-1">{m.title}</h3>
                          <p className="text-sm text-foreground-500">{m.desc}</p>
                        </div>
                      </div>
                      <div className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center">
                        <span className="text-[10px] font-bold text-white">{m.year.slice(2)}</span>
                      </div>
                      <div className="flex-1 md:hidden">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-accent-600">{m.year}</span>
                          <h3 className="font-heading text-base text-foreground-950">{m.title}</h3>
                        </div>
                        <p className="text-sm text-foreground-500">{m.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12 md:mb-14">
                <span className="inline-block px-4 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                  联系我们
                </span>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
                  期待与您沟通
                </h2>
                <p className="text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
                  如有任何问题、建议或合作意向，欢迎随时联系我们
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
                <div className="flex flex-col gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-background-50 border border-background-200/70 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-1">邮箱</h3>
                      <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-background-50 border border-background-200/70 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-1">电话</h3>
                      <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-background-50 border border-background-200/70 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-1">地址</h3>
                      <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                    </div>
                  </div>
                </div>

                <div>
                  {formStatus === 'success' ? (
                    <div className="bg-background-50 border border-background-200/70 rounded-2xl p-8 text-center">
                      <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                        <i className="ri-check-line text-2xl text-primary-600"></i>
                      </div>
                      <h3 className="font-heading text-lg text-foreground-950 mb-2">提交成功！</h3>
                      <p className="text-sm text-foreground-600">感谢您的留言，我们将在24小时内回复您。</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8" data-readdy-form="d9u06il3pcjqs2fcsjt0">
                      <div className="flex flex-col gap-4">
                        <div>
                          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">姓名</label>
                          <input
                            id="contact-name"
                            name="name"
                            type="text"
                            required
                            className="w-full px-4 py-2.5 text-sm rounded-xl border border-background-200/70 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors duration-200"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱</label>
                          <input
                            id="contact-email"
                            name="email"
                            type="email"
                            required
                            className="w-full px-4 py-2.5 text-sm rounded-xl border border-background-200/70 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors duration-200"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">主题</label>
                          <input
                            id="contact-subject"
                            name="subject"
                            type="text"
                            required
                            className="w-full px-4 py-2.5 text-sm rounded-xl border border-background-200/70 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors duration-200"
                            placeholder="请输入主题"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">留言</label>
                          <textarea
                            id="contact-message"
                            name="message"
                            rows={4}
                            required
                            maxLength={500}
                            className="w-full px-4 py-2.5 text-sm rounded-xl border border-background-200/70 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors duration-200 resize-none"
                            placeholder="请输入您的留言（500字以内）"
                          ></textarea>
                        </div>
                        <input
                          type="text"
                          name="phone_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                          className="honeypot-field"
                        />
                        {formStatus === 'error' && (
                          <p className="text-sm text-red-600">{formError}</p>
                        )}
                        <button
                          type="submit"
                          className="w-full px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-xl hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                        >
                          发送留言
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}